import { LogoFidexaPrincipal } from "@/assets";
import { Button, buttonVariants } from "@/components";
import { PublicRoutes } from "@/constants/routes";
import { cn } from "@/utils";
import { t } from "@/context/language";
import { AnimatePresence, motion } from "framer-motion";
import {
  Blocks,
  Calculator,
  LogIn,
  Megaphone,
  Menu,
  Newspaper,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { openWhatsApp } from "../utils/whatsapp";
import { IntegrationsMenu } from "./integrationsMenu";
import { NewsroomMenu } from "./newsroomMenu";
import { ToolsMenu } from "./toolsMenu";

type HeaderProps = {
  /** Quando informado, substitui o comportamento padrão (ir direto pro WhatsApp) do CTA "especialista". */
  onSpecialistClick?: () => void;
  /**
   * "minimal": só a logo, sem nav/CTA/idioma/login/menu — usado em LPs de
   * marketing com foco único nos CTAs da própria página (ex.: régua de
   * cobrança), onde qualquer saída do funil é indesejada.
   */
  variant?: "default" | "minimal";
};

export function Header({
  onSpecialistClick,
  variant = "default",
}: HeaderProps = {}) {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      if (currentScrollY > 0) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }

      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  if (variant === "minimal") {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.header
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={`fixed top-0 right-0 left-0 z-50 bg-white/95 px-4 py-4 backdrop-blur-sm sm:px-6 lg:px-12 lg:py-5 ${
              hasScrolled ? "border-b border-gray-200" : ""
            }`}
          >
            <div className="mx-auto flex max-w-6xl items-center">
              <Link
                to="/"
                aria-label="Fidexa — página inicial"
                className="flex min-h-11 shrink-0 items-center"
              >
                <LogoFidexaPrincipal className="h-7 w-auto lg:h-9" />
              </Link>
            </div>
          </motion.header>
        )}
      </AnimatePresence>
    );
  }

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.header
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={`fixed top-0 right-0 left-0 z-50 bg-white/95 px-4 py-4 backdrop-blur-sm sm:px-6 lg:px-12 lg:py-5 ${
              hasScrolled ? "border-b border-gray-200" : ""
            }`}
          >
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-6 lg:gap-8">
                <Link
                  to="/"
                  aria-label="Fidexa — página inicial"
                  className="flex min-h-11 shrink-0 items-center"
                >
                  <LogoFidexaPrincipal className="h-7 w-auto lg:h-9" />
                </Link>

                <nav
                  aria-label="Navegação principal"
                  className="relative hidden items-center gap-8 lg:flex"
                >
                  <Link
                    to={PublicRoutes.BLOG}
                    className="text-ds-ink-900 hover:text-ds-ink-600 cursor-pointer text-base font-medium transition-colors"
                  >
                    Blog
                  </Link>

                  <NewsroomMenu />

                  <ToolsMenu />

                  <IntegrationsMenu />
                </nav>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <Button
                  onClick={() =>
                    onSpecialistClick
                      ? onSpecialistClick()
                      : openWhatsApp("header")
                  }
                  className="hidden h-11 w-auto rounded-full whitespace-nowrap sm:inline-flex sm:px-5 sm:text-base"
                >
                  {t.header.specialist}
                </Button>

                {/* Desktop: botão de login visível */}
                <div className="hidden items-center gap-3 lg:flex">
                  {/* Link com cara de botão — e não <Link><Button>, que aninha
                      dois interativos. */}
                  <Link
                    to="/auth"
                    className={cn(
                      buttonVariants({ variant: "ghost" }),
                      "h-11 w-auto rounded-full border border-gray-300 bg-white px-5 font-medium text-black hover:bg-gray-50"
                    )}
                  >
                    {t.header.login}
                  </Link>
                </div>

                {/* Mobile/tablet: menu-hamburguer com Blog, Newsroom, Ferramentas, Integrações e Entrar */}
                <div ref={menuRef} className="relative shrink-0 lg:hidden">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(o => !o)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-label={t.header.menu}
                    className="flex size-11 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white text-black transition-colors hover:bg-gray-50"
                  >
                    {menuOpen ? (
                      <X className="size-5" />
                    ) : (
                      <Menu className="size-5" />
                    )}
                  </button>

                  {menuOpen && (
                    <div
                      role="menu"
                      className="absolute top-full right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white py-2 shadow-[0_8px_24px_#00000018]"
                    >
                      <Link
                        to={PublicRoutes.BLOG}
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="text-ds-ink-900 hover:bg-ds-ink-100 flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors"
                      >
                        <Newspaper className="text-ds-ink-500 size-4" />
                        Blog
                      </Link>

                      <Link
                        to={PublicRoutes.NEWSROOM}
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="text-ds-ink-900 hover:bg-ds-ink-100 flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors"
                      >
                        <Megaphone className="text-ds-ink-500 size-4" />
                        {t.header.nav.press}
                      </Link>

                      <Link
                        to={PublicRoutes.FERRAMENTAS}
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="text-ds-ink-900 hover:bg-ds-ink-100 flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors"
                      >
                        <Calculator className="text-ds-ink-500 size-4" />
                        Ferramentas
                      </Link>

                      <Link
                        to={PublicRoutes.INTEGRACOES}
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="text-ds-ink-900 hover:bg-ds-ink-100 flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors"
                      >
                        <Blocks className="text-ds-ink-500 size-4" />
                        Integrações
                      </Link>

                      <Link
                        to="/auth"
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="text-ds-ink-900 hover:bg-ds-ink-100 flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors"
                      >
                        <LogIn className="text-ds-ink-500 size-4" />
                        {t.header.login}
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.header>
        )}
      </AnimatePresence>
    </>
  );
}
